// Open questions (Blue Market only): the latest round's and the spec's questions, answered into decisions.md.
import { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, put } from '../api';
import { useAuth } from '../auth';
import { Menu, ago, useConfirm, useToast } from '../ui';
import type { ClientQuestion, ClientQuestions, Project, Question, Questions } from '../types';

type Filter = 'open' | 'answered' | 'asked' | 'all';
type Drafts = Record<string, string>;

const sentence = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

// Unsaved answers stay on this device until they're saved (an iPad can lose a tab).
const draftKey = (projectId: string) => `bmp.answers.${projectId}`;
function loadDrafts(projectId: string): Drafts {
  try { return JSON.parse(localStorage.getItem(draftKey(projectId)) || '{}'); } catch { return {}; }
}
function storeDrafts(projectId: string, drafts: Drafts) {
  try {
    const kept = Object.fromEntries(Object.entries(drafts).filter(([, v]) => v.trim()));
    if (Object.keys(kept).length) localStorage.setItem(draftKey(projectId), JSON.stringify(kept)); else localStorage.removeItem(draftKey(projectId));
  } catch { /* private mode */ }
}

/** The old address: questions now live in a tab on the project page. */
export function QuestionsPage() {
  const { id = '' } = useParams();
  return <Navigate to={`/projects/${id}?tab=questions`} replace />;
}

/** Blue Market's Questions tab on a project page. */
export function QuestionsPanel({ project }: { project: Project }) {
  const id = project.id;
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { data } = useQuery({ queryKey: ['questions', id], queryFn: () => get<Questions>(`/api/projects/${id}/questions`) });
  const [filter, setFilter] = useState<Filter>('open');
  const [drafts, setDraftsState] = useState<Drafts>(() => loadDrafts(id));
  const [editing, setEditing] = useState<Set<string>>(new Set());
  const setDraft = (key: string, value: string) => setDraftsState((d) => { const next = { ...d, [key]: value }; storeDrafts(id, next); return next; });
  const clearDrafts = (keys: string[]) => {
    setDraftsState((d) => { const next = { ...d }; keys.forEach((k) => delete next[k]); storeDrafts(id, next); return next; });
    setEditing((e) => { const next = new Set(e); keys.forEach((k) => next.delete(k)); return next; });
  };

  const save = useMutation({
    mutationFn: (answers: { key: string; answer: string; kind: 'answer' | 'assumption' }[]) => put<Questions>(`/api/projects/${id}/questions/answers`, { answers }),
    onSuccess: (q, answers) => {
      qc.setQueryData(['questions', id], q); qc.invalidateQueries({ queryKey: ['spec-handoff', id] });
      clearDrafts(answers.map((a) => a.key));
      toast(answers.length === 1 ? 'Saved to decisions.md' : `Saved ${answers.length} answers to decisions.md`);
    },
    onError: (e: Error) => toast(e.message, true),
  });
  const remove = useMutation({
    mutationFn: (key: string) => del<Questions>(`/api/projects/${id}/questions/${encodeURIComponent(key)}/answer`),
    onSuccess: (q) => { qc.setQueryData(['questions', id], q); qc.invalidateQueries({ queryKey: ['spec-handoff', id] }); },
    onError: (e: Error) => toast(e.message, true),
  });

  const all = useMemo(() => data ? [...data.questions, ...data.resolved] : [], [data]);
  const visible = all.filter((q) => filter === 'all' || (filter === 'open' ? !q.answer : filter === 'asked' ? !!q.client : !!q.answer));
  const dirty = all.filter((q) => (drafts[q.key] ?? '').trim() && (drafts[q.key].trim() !== q.answer?.answer));
  const editable = can('delivery.edit');

  const saveAll = () => save.mutate(dirty.map((q) => ({ key: q.key, answer: drafts[q.key], kind: q.answer?.kind ?? 'answer' })));
  const removeAnswer = async (q: Question) => {
    if (await confirm(`Remove the ${q.answer?.kind ?? 'answer'} for "${q.topic}"?`, { body: 'Its row is removed from decisions.md, so the question is open again.', ok: 'Remove', danger: true })) remove.mutate(q.key);
  };

  if (!data) return <span className="spin muted" />;
  return (
    <div>
      <div className="panel-bar">
        <div className="seg" role="group" aria-label="Show">
          {(['open', 'answered', 'asked', 'all'] as Filter[]).map((f) => {
            const n = f === 'open' ? data.open : f === 'answered' ? all.filter((q) => q.answer).length : f === 'asked' ? data.asked : all.length;
            const label = f === 'asked' ? 'Asked the client' : f[0].toUpperCase() + f.slice(1);
            return <button key={f} className={filter === f ? 'on' : ''} aria-pressed={filter === f} onClick={() => setFilter(f)}>{label} <span className="seg-n">{n}</span></button>;
          })}
        </div>
        <span className="grow" />
        {editable && dirty.length > 0 && <button className="btn sm primary" onClick={saveAll} disabled={save.isPending}>{save.isPending && <span className="spin" />}Save all ({dirty.length})</button>}
      </div>
      <p className="muted tiny" style={{ margin: '0 0 12px', maxWidth: 760 }}>
        {data.round ? <>From the v{data.round} notes{data.questions.some((q) => q.source === 'spec') ? ' and the tech spec' : ''}. </> : 'No design rounds yet. '}
        Answers go into <span className="mono">decisions.md</span>, so the next round and the spec follow them. <b>Ask the client</b> shows a question to their admins and review links.
      </p>
      {dirty.length > 0 && <p className="muted tiny" style={{ margin: '-6px 0 12px' }}>Unsaved answers are kept on this device until you save them. <b>Save all</b> keeps each one's kind: new ones save as answers.</p>}
      <div className="stack" style={{ gap: 12 }}>
        {visible.map((q) => {
          const isEditing = editable && (!q.answer || editing.has(q.key));
          const draft = drafts[q.key] ?? (editing.has(q.key) ? q.answer?.answer ?? '' : '');
          const unsaved = !!draft.trim() && draft.trim() !== q.answer?.answer;
          const status = !q.answer ? { label: 'Open', badge: 'warn' } : q.answer.kind === 'answer' ? { label: 'Answered', badge: 'ok' } : { label: 'Assumption', badge: 'brand' };
          return (
            <section key={q.key} className={`card question ${q.answer ? q.answer.kind : 'open'}`}>
              <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="row wrap" style={{ gap: 6 }}>
                    <b>{q.topic}</b>
                    {q.source === 'spec' && <span className="badge tiny-badge">From the spec</span>}
                    {q.source === 'decisions' && <span className="badge tiny-badge" title="The latest notes no longer list this question">Settled in the designs</span>}
                  </div>
                  {q.text && q.text !== q.topic && <p className="small" style={{ margin: '4px 0 0' }}>{sentence(q.text)}</p>}
                  {q.assumed && !q.answer && <p className="small" style={{ margin: '6px 0 0' }}><span className="muted">The spec assumes:</span> {sentence(q.assumed)}</p>}
                </div>
                <div className="row" style={{ gap: 6, flex: 'none' }}>
                  {unsaved && <span className="badge warn">Unsaved</span>}
                  <span className={`badge ${status.badge}`}>{status.label}</span>
                </div>
              </div>
              <AskClient q={q} projectId={id} editable={editable} />
              {q.answer && !isEditing && (
                <div style={{ marginTop: 10 }}>
                  <div className="answer">{q.answer.answer}</div>
                  <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
                    <span className="muted tiny grow">{q.answer.date}{q.answer.round ? ` · ${q.answer.round}` : ''}</span>
                    {editable && <>
                      {q.answer.kind === 'assumption' && <button className="btn sm ghost" onClick={() => setEditing((e) => new Set(e).add(q.key))}>Confirm or change</button>}
                      {q.answer.kind === 'answer' && <button className="btn sm ghost" onClick={() => setEditing((e) => new Set(e).add(q.key))}>Edit</button>}
                      <button className="btn sm ghost danger" onClick={() => removeAnswer(q)}>Remove</button>
                    </>}
                  </div>
                </div>
              )}
              {isEditing && (
                <form className="stack" style={{ gap: 8, marginTop: 10 }} onSubmit={(e) => { e.preventDefault(); if (draft.trim()) save.mutate([{ key: q.key, answer: draft, kind: 'answer' }]); }}>
                  <textarea className="input" rows={2} value={draft} maxLength={5000} onChange={(e) => setDraft(q.key, e.target.value)}
                            placeholder={q.assumed ? `The client's answer, or keep the spec's assumption` : "The client's answer, or your best assumption"} />
                  <div className="row wrap" style={{ justifyContent: 'flex-end', gap: 8 }}>
                    {q.assumed && !draft && <button type="button" className="btn sm ghost" onClick={() => setDraft(q.key, q.assumed)}>Use the spec's assumption</button>}
                    {q.answer && <button type="button" className="btn sm ghost" onClick={() => clearDrafts([q.key])}>Cancel</button>}
                    <button type="button" className="btn sm" disabled={!draft.trim() || save.isPending} title="Not confirmed by the client yet"
                            onClick={() => save.mutate([{ key: q.key, answer: draft, kind: 'assumption' }])}>Save as assumption</button>
                    <button className="btn sm primary" disabled={!draft.trim() || save.isPending}>Save answer</button>
                  </div>
                </form>
              )}
            </section>
          );
        })}
        {!visible.length && <div className="card empty">{filter === 'open' ? (all.length ? 'No open questions: everything has an answer or an assumption.' : 'No open questions in the latest notes.') : 'Nothing here yet.'}</div>}
      </div>
      {dialog}
    </div>
  );
}

// ---------- asking the client ----------

function AskClient({ q, projectId, editable }: { q: Question; projectId: string; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [wording, setWording] = useState<string | null>(null);
  const onSuccess = (data: Questions) => { qc.setQueryData(['questions', projectId], data); setWording(null); };
  const onError = (e: Error) => toast(e.message, true);
  const ask = useMutation({ mutationFn: (text: string) => put<Questions>(`/api/projects/${projectId}/questions/${encodeURIComponent(q.key)}/ask`, { text }),
                            onSuccess: (d) => { onSuccess(d); toast(q.client ? 'Wording updated' : 'Asked the client'); }, onError });
  const stop = useMutation({ mutationFn: () => del<Questions>(`/api/projects/${projectId}/questions/${encodeURIComponent(q.key)}/ask`), onSuccess, onError });
  if (q.source === 'decisions' && !q.client) return null;          // settled in the designs: nothing to ask

  if (wording !== null) return (
    <form className="ask-box stack" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); if (wording.trim()) ask.mutate(wording); }}>
      <label className="field" style={{ margin: 0 }}><span>What the client sees</span>
        <textarea className="input" rows={2} value={wording} maxLength={2000} onChange={(e) => setWording(e.target.value)} autoFocus />
        <span className="hint">Under the heading "{q.topic}". Rewrite it for them if the notes are terse or internal.</span>
      </label>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn sm ghost" onClick={() => setWording(null)}>Cancel</button>
        <button className="btn sm primary" disabled={!wording.trim() || ask.isPending}>{q.client ? 'Save wording' : 'Ask the client'}</button>
      </div>
    </form>
  );
  if (!q.client) return editable ? (
    <div className="row" style={{ marginTop: 8 }}>
      <button className="btn sm ghost" style={{ paddingLeft: 0 }} onClick={() => setWording(sentence(q.text && q.text !== q.topic ? q.text : q.topic))}>Ask the client</button>
    </div>
  ) : null;
  const c = q.client;
  return (
    <div className="ask-box row wrap" style={{ gap: 8 }}>
      <span className={`badge ${c.answered_at ? 'ok' : 'info'}`}>{c.answered_at ? `Answered by ${c.answered_by}${c.via_link ? ' (review link)' : ''}` : 'Asked the client'}</span>
      <span className="muted tiny grow">{c.answered_at ? ago(c.answered_at) : `by ${c.asked_by} · ${ago(c.asked_at)}`}{c.text !== q.text ? ` · "${c.text}"` : ''}</span>
      {editable && <Menu label={`Asking the client: ${q.topic}`} items={[
        { label: 'Change the wording', onClick: () => setWording(c.text) },
        { label: 'Stop asking', danger: true, onClick: async () => {
          if (await confirm(`Stop asking the client about "${q.topic}"?`, { body: 'It disappears from their list. Any answer they gave stays in decisions.md.', ok: 'Stop asking', danger: true })) stop.mutate();
        } },
      ]} />}
      {dialog}
    </div>
  );
}

// ================= the client's side: questions Blue Market asked them =================

/** Where a client's questions live: the signed-in API, or the review-link API (/rapi). */
export interface ClientQuestionsApi { key: unknown[]; list: string; item: (key: string) => string }

export const projectQuestionsApi = (projectId: string): ClientQuestionsApi => ({
  key: ['client-questions', projectId], list: `/api/projects/${projectId}/client-questions`,
  item: (key) => `/api/projects/${projectId}/client-questions/${encodeURIComponent(key)}`,
});

export const REVIEW_QUESTIONS: ClientQuestionsApi = { key: ['review-questions'], list: '/rapi/questions', item: (key) => `/rapi/questions/${encodeURIComponent(key)}` };

export function useClientQuestions(api: ClientQuestionsApi, enabled = true) {
  return useQuery({ queryKey: api.key, queryFn: () => get<ClientQuestions>(api.list), enabled });
}

function ClientQuestionCard({ q, api, canAnswer }: { q: ClientQuestion; api: ClientQuestionsApi; canAnswer: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<string | null>(q.answer === null ? '' : null);
  const send = useMutation({
    mutationFn: (answer: string) => put<ClientQuestions>(api.item(q.key), { answer }),
    onSuccess: (data) => { qc.setQueryData(api.key, data); setDraft(null); toast('Thanks, your answer has been sent to Blue Market'); },
    onError: (e: Error) => toast(e.message, true),
  });
  const editing = canAnswer && draft !== null;
  return (
    <section className={`card question ${q.answer === null ? 'open' : 'answer'}`}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div className="grow" style={{ minWidth: 0 }}>
          <b>{q.topic}</b>
          {q.text && q.text !== q.topic && <p className="small" style={{ margin: '4px 0 0' }}>{sentence(q.text)}</p>}
        </div>
        <span className={`badge ${q.answer === null ? 'warn' : 'ok'}`} style={{ flex: 'none' }}>{q.answer === null ? 'Waiting for you' : 'Answered'}</span>
      </div>
      {q.answer !== null && !editing && (
        <div style={{ marginTop: 10 }}>
          <div className="answer">{q.answer}</div>
          <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
            <span className="muted tiny grow">Answered by {q.answered_by}{q.answered_at ? ` · ${ago(q.answered_at)}` : ''}</span>
            {canAnswer && <button className="btn sm ghost" onClick={() => setDraft(q.answer ?? '')}>Change answer</button>}
          </div>
        </div>
      )}
      {editing && (
        <form className="stack" style={{ gap: 8, marginTop: 10 }} onSubmit={(e) => { e.preventDefault(); if (draft.trim()) send.mutate(draft); }}>
          <textarea className="input" rows={3} value={draft} maxLength={5000} onChange={(e) => setDraft(e.target.value)} placeholder="Your answer" aria-label={`Your answer: ${q.topic}`} />
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            {q.answer !== null && <button type="button" className="btn sm ghost" onClick={() => setDraft(null)}>Cancel</button>}
            <button className="btn sm primary" disabled={!draft.trim() || send.isPending}>{send.isPending && <span className="spin" />}Send answer</button>
          </div>
        </form>
      )}
      {q.answer === null && !canAnswer && <p className="muted tiny" style={{ margin: '8px 0 0' }}>Your admin can answer this.</p>}
    </section>
  );
}

/** The list itself: open questions first, then answered ones. */
export function ClientQuestionsList({ api, data }: { api: ClientQuestionsApi; data: ClientQuestions }) {
  const open = data.questions.filter((q) => q.answer === null);
  const answered = data.questions.filter((q) => q.answer !== null);
  return (
    <div className="stack" style={{ gap: 12 }}>
      {open.map((q) => <ClientQuestionCard key={q.key} q={q} api={api} canAnswer={data.can_answer} />)}
      {!open.length && <p className="muted small" style={{ margin: 0 }}>Nothing waiting for you. Thank you!</p>}
      {answered.length > 0 && <div className="eyebrow" style={{ marginTop: 6 }}>Answered</div>}
      {answered.map((q) => <ClientQuestionCard key={q.key} q={q} api={api} canAnswer={data.can_answer} />)}
    </div>
  );
}

/** On a client user's project page. */
export function ClientQuestionsSection({ project }: { project: Project }) {
  const api = useMemo(() => projectQuestionsApi(project.id), [project.id]);
  const { data } = useClientQuestions(api);
  if (!data || !data.questions.length) return null;
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h2>Questions for you</h2>
        {data.open > 0 && <span className="badge warn">{data.open} waiting</span>}
      </div>
      <div style={{ padding: '14px 18px' }}>
        <p className="muted small" style={{ margin: '0 0 12px' }}>Blue Market needs these answered to finish the designs and plan the build. Your answers go straight to the team.</p>
        <ClientQuestionsList api={api} data={data} />
      </div>
    </section>
  );
}
